import type { RoseRocketClient } from '../clients/roseRocketClient.interface';
import type { GoogleSheetsWriter } from '../clients/googleSheetsClient';
import { mapCustomer } from '../mappers/customerMapper';
import { mapInvoice } from '../mappers/invoiceMapper';
import { mapOrder } from '../mappers/orderMapper';
import { SHEET_HEADERS } from '../types/sheets';
import type { AppConfig } from '../config/env';
import type { SheetRow, SyncSummary } from '../types/entities';
import { validateCustomer } from '../validation/validateCustomer';
import { validateInvoice } from '../validation/validateInvoice';
import { validateOrder } from '../validation/validateOrder';
import { syncEntity } from './syncEntity';

export async function runSync(params: {
  config: AppConfig;
  roseRocket: RoseRocketClient;
  sheets: GoogleSheetsWriter;
  dryRun?: boolean;
  logger?: { info(message: string, meta?: unknown): void; warn(message: string, meta?: unknown): void; error(message: string, meta?: unknown): void };
}): Promise<SyncSummary[]> {
  const runId = `sync_${Date.now()}`;
  const summaries: SyncSummary[] = [];
  params.logger?.info('Sync started', { runId, dryRun: params.dryRun });

  if (params.config.syncEntities.includes('orders')) {
    summaries.push(await syncEntity({
      runId,
      entity: 'orders',
      sheetName: 'Orders',
      headers: SHEET_HEADERS.Orders,
      keyField: 'rr_order_id',
      records: await params.roseRocket.getOrders(params.config.updatedSince),
      sheets: params.sheets,
      validate: validateOrder,
      map: mapOrder,
      dryRun: params.dryRun,
      logger: params.logger,
    }));
  }

  if (params.config.syncEntities.includes('customers')) {
    summaries.push(await syncEntity({
      runId,
      entity: 'customers',
      sheetName: 'Customers',
      headers: SHEET_HEADERS.Customers,
      keyField: 'rr_customer_id',
      records: await params.roseRocket.getCustomers(params.config.updatedSince),
      sheets: params.sheets,
      validate: validateCustomer,
      map: mapCustomer,
      dryRun: params.dryRun,
      logger: params.logger,
    }));
  }

  if (params.config.syncEntities.includes('invoices')) {
    summaries.push(await syncEntity({
      runId,
      entity: 'invoices',
      sheetName: 'Invoices',
      headers: SHEET_HEADERS.Invoices,
      keyField: 'rr_invoice_id',
      records: await params.roseRocket.getInvoices(params.config.updatedSince),
      sheets: params.sheets,
      validate: validateInvoice,
      map: mapInvoice,
      dryRun: params.dryRun,
      logger: params.logger,
    }));
  }

  if (!params.dryRun) {
    const logRows: SheetRow[] = summaries.map((summary) => ({
      run_id: summary.runId,
      started_at: summary.startedAt,
      finished_at: summary.finishedAt,
      entity: summary.entity,
      status: summary.status,
      fetched_count: summary.fetchedCount,
      created_count: summary.createdCount,
      updated_count: summary.updatedCount,
      skipped_count: summary.skippedCount,
      failed_count: summary.failedCount,
      message: summary.message,
      error_details: summary.errorDetails,
    }));
    await params.sheets.ensureSheetHeaders('Sync Logs', SHEET_HEADERS['Sync Logs']);
    await params.sheets.appendRows('Sync Logs', SHEET_HEADERS['Sync Logs'], logRows);
  }

  params.logger?.info('Sync finished', { runId, summaries });
  return summaries;
}
